import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { IAPIErrorObject } from '@shared/interfaces/api.interface';
import { ICandidate } from '@shared/interfaces/candidate.interface';
import { IReference } from '@shared/interfaces/reference.interface';
import { IReminder } from '@shared/interfaces/reminder.interface';
import { ISetting } from '@shared/interfaces/setting.interface';
import { CandidatesService } from '@shared/services/candidates.service';
import { LoggerService } from '@shared/services/logger.service';
import { ReferencesService } from '@shared/services/references.service';
import { ToastService } from '@shared/services/toast.service';
import moment from 'moment';

import { SessionStorage } from 'ngx-webstorage';
import { Subject, Subscription } from 'rxjs';

@Component({
    selector: 'app-sidebar-reference-question-set-alerts',
    templateUrl: './sidebar.reference.question-set.alerts.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SidebarReferenceQuestionSetAlertsComponent
  implements OnInit, OnDestroy
{
  @Input() public reference: IReference;

  @SessionStorage() public settings: ISetting;

  public title = String('REFERENCE.TITLE');
  public error: IAPIErrorObject;
  public sharingWarning: string;
  public answers: any[] = [];

  public isApproving = Boolean(false);
  public isPending = Boolean(false);
  public isReminderDisabled = Boolean(false);
  public isClearingIpConflict = Boolean(false);

  public openClearIpConflictModal: Subject<void> = new Subject<void>();
  public closeClearIpConflictModal: Subject<void> = new Subject<void>();

  private readonly constructorName: string = String(this.constructor.name);

  private referencesRefreshSubscription: Subscription;

  constructor(
    private readonly _candidates: CandidatesService,
    private readonly _logger: LoggerService,
    private readonly _references: ReferencesService,
    private readonly _toast: ToastService
  ) {
    this.clearIpConflict = this.clearIpConflict.bind(this);
  }

  ngOnInit(): void {
    const url = 'Reference refresh';
    this.referencesRefreshSubscription =
      this._references.referencesCalled$.subscribe(
        (res: IReference) => {
          this._logger.info(this.constructorName, url, res);

          if (res) {
            this.reference = res;
            this.answers = this.reference?.answer_set?.answers || [];
            this.setPermissionWarning();
          }
        },
        (err: any) => this._logger.error(this.constructorName, url, err)
      );

    this.isPending = this.reference?.is_pending && !this.reference?.filled_at;
    this.answers = this.reference?.answer_set?.answers || [];

    this.setPermissionWarning();
  }

  ngOnDestroy(): void {
    this.referencesRefreshSubscription?.unsubscribe();
  }

  public setPermissionWarning(): void {
    const candidatePermission = this.reference.permission_share_candidate;
    const clientsPermission = this.reference.permission_share_clients;
    const warningPathPrefix = 'REFERENCE.PERMISSION.WARNING.';

    if (!candidatePermission && !clientsPermission) {
      this.sharingWarning = warningPathPrefix + 'BOTH';
    } else if (!candidatePermission) {
      this.sharingWarning = warningPathPrefix + 'CANDIDATE';
    } else if (!clientsPermission) {
      this.sharingWarning = warningPathPrefix + 'CLIENTS';
    } else {
      this.sharingWarning = '';
    }
  }

  public approveReference() {
    this.isApproving = true;

    const url = `Approve ${this.reference.id}`;
    this._references.approveReference(this.reference.id).subscribe(
      (res: IReference) => {
        this._logger.info(this.constructorName, url, res);

        this._toast.success('TOASTS.REFERENCE_APPROVED');
        this.reference = res;
        this.isPending = false;

        this.isApproving = false;
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);

        this.error = err[0];
        this.isApproving = false;
      }
    );
  }

  public sendReferenceReminder() {
    this.isReminderDisabled = true;

    const url = `GET /reminders/references/${this.reference.id}`;
    this._references.sendReferenceReminder(this.reference.id).subscribe(
      (res: Partial<IReminder>) => {
        this._logger.info(this.constructorName, url, res);

        this._toast.success('TOASTS.REMINDER_SENT');

        this.reference.reminder = {
          ...this.reference.reminder,
          latest_sent_at: moment().format(),
          rate_exceeded: true
        };
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);

        this._toast.error(err[0].detail);
      }
    );
  }

  public openClearIpConflictModalFn(): void {
    this.openClearIpConflictModal.next();
  }

  public clearIpConflict(type: string): void {
    if (type !== 'DELETE') {
      return;
    }

    this.isClearingIpConflict = true;

    const url = `DELETE /references/${this.reference.id}/ip_conflict`;
    this._references.clearIpConflict(this.reference.id).subscribe(
      () => {
        this._logger.info(this.constructorName, url, null);
        this.reference.has_same_ip = false;
        this._references.refresh(this.reference);
        this._candidates.refresh(this.reference.candidate as ICandidate);
        this.closeClearIpConflictModal.next();
        this._toast.success('TOASTS.IP_FRAUD_ALERT_CLEARED');
        this.isClearingIpConflict = false;
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
        const detail = err?.[0]?.detail || 'Failed to clear alert';
        this._toast.error(detail);
        this.closeClearIpConflictModal.next();
        this.isClearingIpConflict = false;
      }
    );
  }
}
